import {useMutation} from '@apollo/client'
import {useEffect, useState, FormEvent} from 'react'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {UPDATE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {IResUpdateCommissionReport, NodeCommissionReport} from '../../../types/commissionReportApi'
import {parseId} from '../../../helpers'

interface IModalEditProps {
  isOpen: boolean
  onClose: () => void
  infoEdit: NodeCommissionReport | null
  refetch: () => void
}

export const ModalEditCommissionReport: React.FC<IModalEditProps> = ({isOpen, onClose, infoEdit, refetch}) => {
  const intl = useIntl()
  const [formData, setFormData] = useState({
    name: '',
    paidDate: '',
    approvePreviousYear: false,
  })

  const [gqlUpdate, {loading, data, error}] = useMutation<IResUpdateCommissionReport>(UPDATE_COMMISSION_REPORT)

  useEffect(() => {
    if (infoEdit) {
      setFormData({
        name: infoEdit.name || '',
        paidDate: infoEdit.paidDate?.slice(0, 10) || '', // formato YYYY-MM-DD
        approvePreviousYear: infoEdit.approvePreviousYear || false,
      })
    }
  }, [infoEdit])

  useEffect(() => {
    if (!data) return
    const res = data.commissionReportUpdateMutation
    if (res?.success) {
      toast.success(intl.formatMessage({id: 'commissions.reports.edit.toast.success', defaultMessage: 'Commission Report updated successfully'}))
      refetch()
      onClose()
    } else if (res?.errors?.length) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: res.errors[0]?.message}))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!infoEdit) return

    gqlUpdate({
      variables: {
        id: parseId(infoEdit.id),
        input: {
          name: formData.name,
          paidDate: formData.paidDate || null,
          approvePreviousYear: formData.approvePreviousYear,
        },
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='commissions.reports.edit.title' defaultMessage='Edit Commission Report' />
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>
              <FormattedMessage id='commissions.reports.field.reportTitleShort' defaultMessage='Report title' />
            </Form.Label>
            <Form.Control
              type='text'
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              <FormattedMessage id='commissions.reports.field.paidOn' defaultMessage='Paid on' />
            </Form.Label>
            <Form.Control
              type='date'
              value={formData.paidDate}
              onChange={e => handleChange('paidDate', e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Check
              type='switch'
              id='approvePreviousYearSwitchEdit'
              label={intl.formatMessage({id: 'commissions.reports.approvePrevYear', defaultMessage: 'Approve policies found in previous year'})}
              checked={formData.approvePreviousYear}
              onChange={e => handleChange('approvePreviousYear', e.target.checked)}
            />
            <div className='form-text'>
              <FormattedMessage id='commissions.reports.approvePrevYearHint' defaultMessage='If enabled, policies found in the previous year will be marked as APPROVED_DISCREPANCY instead of NOT_FOUND.' />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <FormattedMessage id='common.action.save' defaultMessage='Save' />
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
